import { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import {
  Home, TrendingUp, Wrench, Settings2, Calculator,
  Download, Plus, MoreVertical, ChevronRight, Edit2, Trash2, Eye, Save, X
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

// Données par défaut alignées sur l'image (14 panneaux)
const QUARTIERS = ['Yopougon', 'Cocody', 'Plateau', 'Marcory', 'Treichville', 'Abobo', 'Koumassi', 'Riviera', 'Port-Bouët', 'Songon', 'Anyama', 'Bingerville', 'Grand-Bassam', 'Adjamé'];
const DEFAULT_PANNEAUX = Array.from({ length: 14 }, (_, i) => {
  const types = ['4x3', '6x4', '8x3'];
  const type = types[i % 3];
  // Coûts différenciés selon le type
  const cout_achat = type === '4x3' ? 12500000 : type === '6x4' ? 18000000 : 15000000;
  const frais_install_montant = type === '4x3' ? 1500000 : type === '6x4' ? 1800000 : 1700000;
  const frais_fonct_montant = type === '4x3' ? 1050000 : type === '6x4' ? 1200000 : 1100000;
  const day = ((i * 3 + 15) % 28) + 1;
  const month = Math.floor(i / 5) + 1;
  const dayPad = String(day).padStart(2, '0');
  const monthPad = String(month).padStart(2, '0');
  const periodes = ['Janv. - Avr. 2024', 'Févr. - Mai 2024', 'Mars - Juin 2024', 'Avr. - Juil. 2024'];
  return {
    id: `panneau-${i + 1}`,
    numero: i + 1,
    nom: `Panneau ${String(i + 1).padStart(2, '0')}`,
    quartier: QUARTIERS[i % QUARTIERS.length],
    type_panneau: type,
    date_achat: `${dayPad}/${monthPad}/2024`,
    cout_achat,
    frais_installation_montant: frais_install_montant,
    frais_installation_date: `${String(day + 5).padStart(2, '0')}/${monthPad}/2024`,
    frais_fonctionnement_montant: frais_fonct_montant,
    frais_fonctionnement_periode: periodes[Math.floor(i / 4) % periodes.length],
    total: cout_achat + frais_install_montant + frais_fonct_montant
  };
});

const TYPE_BADGE_COLORS = {
  '4x3': 'bg-blue-100 text-blue-700 border-blue-200',
  '6x4': 'bg-green-100 text-green-700 border-green-200',
  '8x3': 'bg-orange-100 text-orange-700 border-orange-200'
};

const formatFCFA = (n) => {
  if (n === null || n === undefined || n === '') return '—';
  const num = typeof n === 'number' ? n : parseFloat(n);
  if (isNaN(num)) return '—';
  return num.toLocaleString('fr-FR').replace(/,/g, ' ');
};

export default function SuiviPanneaux({ user, onLogout }) {
  const [panneaux, setPanneaux] = useState(DEFAULT_PANNEAUX);
  const [loading, setLoading] = useState(true);
  const [filterPanneau, setFilterPanneau] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [editingPanneau, setEditingPanneau] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newDepense, setNewDepense] = useState({
    panneau_id: '',
    type_depense: 'installation',
    montant: '',
    date: '',
    notes: ''
  });

  useEffect(() => {
    loadPanneaux();
  }, []);

  const loadPanneaux = async () => {
    try {
      const response = await axios.get(`${API}/api/suivi-panneaux`);
      if (response.data && response.data.length > 0) {
        // Mapper les données backend ou utiliser les défauts si format ancien
        const validTypes = ['4x3', '6x4', '8x3'];
        const mapped = response.data.map((p, i) => {
          // Privilégier le format moderne ; sinon fallback aux defaults
          const backendType = p.type_panneau_format || p.type_panneau;
          const displayType = validTypes.includes(backendType)
            ? backendType
            : DEFAULT_PANNEAUX[i]?.type_panneau || '4x3';
          return {
            id: p.id || `panneau-${i + 1}`,
            numero: p.numero || (i + 1),
            nom: p.nom || `Panneau ${String(i + 1).padStart(2, '0')}`,
            quartier: p.quartier || DEFAULT_PANNEAUX[i]?.quartier || '—',
            type_panneau: displayType,
            date_achat: p.date_achat || DEFAULT_PANNEAUX[i]?.date_achat || '',
            cout_achat: p.cout_achat ?? DEFAULT_PANNEAUX[i]?.cout_achat ?? 0,
            frais_installation_montant: p.frais_installation_montant ?? DEFAULT_PANNEAUX[i]?.frais_installation_montant ?? 0,
            frais_installation_date: p.frais_installation_date || DEFAULT_PANNEAUX[i]?.frais_installation_date || '',
            frais_fonctionnement_montant: p.frais_fonctionnement_montant ?? DEFAULT_PANNEAUX[i]?.frais_fonctionnement_montant ?? 0,
            frais_fonctionnement_periode: p.frais_fonctionnement_periode || DEFAULT_PANNEAUX[i]?.frais_fonctionnement_periode || '',
            total: 0
          };
        }).map(p => ({ ...p, total: (p.cout_achat || 0) + (p.frais_installation_montant || 0) + (p.frais_fonctionnement_montant || 0) }));
        setPanneaux(mapped);
      }
    } catch (error) {
      console.error('Error loading panneaux:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (panneau) => {
    try {
      const updatedTotal = (parseFloat(panneau.cout_achat) || 0)
        + (parseFloat(panneau.frais_installation_montant) || 0)
        + (parseFloat(panneau.frais_fonctionnement_montant) || 0);
      const payload = { ...panneau, total: updatedTotal };
      
      await axios.put(`${API}/api/suivi-panneaux/${panneau.id}`, payload);
      toast.success(`${panneau.nom} mis à jour avec succès`);
      setShowEditModal(false);
      setEditingPanneau(null);
      loadPanneaux();
    } catch (error) {
      console.error('Error saving panneau:', error);
      // Fallback : sauvegarder en local seulement (pour ne pas bloquer l'utilisateur)
      const updated = panneaux.map(p => 
        p.id === panneau.id 
          ? { ...panneau, total: (parseFloat(panneau.cout_achat) || 0) + (parseFloat(panneau.frais_installation_montant) || 0) + (parseFloat(panneau.frais_fonctionnement_montant) || 0) }
          : p
      );
      setPanneaux(updated);
      toast.success(`${panneau.nom} mis à jour (local)`);
      setShowEditModal(false);
      setEditingPanneau(null);
    }
  };

  const handleAddDepense = async () => {
    if (!newDepense.panneau_id || !newDepense.montant) {
      toast.error('Sélectionnez un panneau et saisissez un montant');
      return;
    }
    try {
      const targetPanneau = panneaux.find(p => p.id === newDepense.panneau_id);
      if (!targetPanneau) {
        toast.error('Panneau introuvable');
        return;
      }
      const updated = { ...targetPanneau };
      const montant = parseFloat(newDepense.montant) || 0;
      
      if (newDepense.type_depense === 'installation') {
        updated.frais_installation_montant = (parseFloat(updated.frais_installation_montant) || 0) + montant;
        if (newDepense.date) updated.frais_installation_date = newDepense.date;
      } else if (newDepense.type_depense === 'fonctionnement') {
        updated.frais_fonctionnement_montant = (parseFloat(updated.frais_fonctionnement_montant) || 0) + montant;
        if (newDepense.date) updated.frais_fonctionnement_periode = newDepense.date;
      } else {
        updated.cout_achat = (parseFloat(updated.cout_achat) || 0) + montant;
      }
      
      await handleSave(updated);
      setShowAddModal(false);
      setNewDepense({ panneau_id: '', type_depense: 'installation', montant: '', date: '', notes: '' });
      toast.success('Dépense ajoutée');
    } catch (error) {
      toast.error('Erreur lors de l\'ajout');
    }
  };

  const handleExport = () => {
    const headers = ['N°', 'Nom', 'Quartier', 'Type', 'Date achat', 'Coût achat', 'Frais install.', 'Date install.', 'Frais fonct.', 'Période fonct.', 'Total'];
    const rows = filteredPanneaux.map(p => [
      p.numero, p.nom, p.quartier, p.type_panneau,
      p.date_achat, p.cout_achat, p.frais_installation_montant, p.frais_installation_date,
      p.frais_fonctionnement_montant, p.frais_fonctionnement_periode, p.total
    ]);
    const csv = [headers, ...rows].map(r => r.join(',')).join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `suivi-panneaux-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Export CSV téléchargé');
  };

  // Filtrage
  const filteredPanneaux = panneaux.filter(p => {
    if (filterPanneau !== 'all' && p.id !== filterPanneau) return false;
    if (filterType !== 'all' && p.type_panneau !== filterType) return false;
    return true;
  });

  // Calculs des totaux
  const totals = filteredPanneaux.reduce((acc, p) => ({
    cout_achat: acc.cout_achat + (parseFloat(p.cout_achat) || 0),
    frais_installation: acc.frais_installation + (parseFloat(p.frais_installation_montant) || 0),
    frais_fonctionnement: acc.frais_fonctionnement + (parseFloat(p.frais_fonctionnement_montant) || 0),
    total: acc.total + (parseFloat(p.total) || 0)
  }), { cout_achat: 0, frais_installation: 0, frais_fonctionnement: 0, total: 0 });

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout} currentPage="suivi">
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} onLogout={onLogout} currentPage="suivi">
      <div className="space-y-6">
        {/* Header avec breadcrumb */}
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Suivis commande panneaux</h1>
          <div className="flex items-center gap-2 text-sm text-gray-500 mt-1">
            <span>Accueil</span>
            <ChevronRight className="h-4 w-4" />
            <span>Suivi</span>
            <ChevronRight className="h-4 w-4" />
            <span className="text-blue-600 font-medium">Suivis commande panneaux</span>
          </div>
        </div>

        {/* 5 KPI Cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          <Card className="border-l-0 shadow-sm">
            <CardContent className="p-5">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs text-gray-500 mb-2">Total panneaux</p>
                  <p className="text-2xl font-bold text-gray-900">{filteredPanneaux.length}</p>
                  <p className="text-xs text-gray-500 mt-1">Panneaux</p>
                </div>
                <div className="w-11 h-11 bg-blue-50 rounded-xl flex items-center justify-center">
                  <Home className="h-5 w-5 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="shadow-sm">
            <CardContent className="p-5">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs text-gray-500 mb-2">Total dépense d'achat</p>
                  <p className="text-xl font-bold text-gray-900" data-testid="total-cout-achat">{formatFCFA(totals.cout_achat)} FCFA</p>
                  <p className="text-xs text-gray-500 mt-1">Coût d'achat total</p>
                </div>
                <div className="w-11 h-11 bg-green-50 rounded-xl flex items-center justify-center">
                  <TrendingUp className="h-5 w-5 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="shadow-sm">
            <CardContent className="p-5">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs text-gray-500 mb-2">Total frais installation</p>
                  <p className="text-xl font-bold text-gray-900">{formatFCFA(totals.frais_installation)} FCFA</p>
                  <p className="text-xs text-gray-500 mt-1">Frais d'installation total</p>
                </div>
                <div className="w-11 h-11 bg-orange-50 rounded-xl flex items-center justify-center">
                  <Wrench className="h-5 w-5 text-orange-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="shadow-sm">
            <CardContent className="p-5">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs text-gray-500 mb-2">Total frais fonctionnement</p>
                  <p className="text-xl font-bold text-gray-900">{formatFCFA(totals.frais_fonctionnement)} FCFA</p>
                  <p className="text-xs text-gray-500 mt-1">Frais de fonctionnement total</p>
                </div>
                <div className="w-11 h-11 bg-emerald-50 rounded-xl flex items-center justify-center">
                  <Settings2 className="h-5 w-5 text-emerald-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="shadow-sm">
            <CardContent className="p-5">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs text-gray-500 mb-2">Total général</p>
                  <p className="text-xl font-bold text-gray-900" data-testid="total-general">{formatFCFA(totals.total)} FCFA</p>
                  <p className="text-xs text-gray-500 mt-1">Dépenses totales</p>
                </div>
                <div className="w-11 h-11 bg-indigo-50 rounded-xl flex items-center justify-center">
                  <Calculator className="h-5 w-5 text-indigo-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters row */}
        <div className="flex flex-wrap gap-3 items-center justify-between">
          <div className="flex flex-wrap gap-3 flex-1">
            <Select value={filterPanneau} onValueChange={setFilterPanneau}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Tous les panneaux" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les panneaux</SelectItem>
                {panneaux.map(p => (
                  <SelectItem key={p.id} value={p.id}>{p.nom} ({p.quartier})</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Tous les types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les types</SelectItem>
                <SelectItem value="4x3">4x3</SelectItem>
                <SelectItem value="6x4">6x4</SelectItem>
                <SelectItem value="8x3">8x3</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex gap-2">
            <Button variant="outline" onClick={handleExport} className="gap-2" data-testid="export-csv-btn">
              <Download className="h-4 w-4" />
              Exporter
            </Button>
            <Button
              className="bg-blue-600 hover:bg-blue-700 gap-2"
              onClick={() => setShowAddModal(true)}
              data-testid="add-depense-btn"
            >
              <Plus className="h-4 w-4" />
              Ajouter une dépense
            </Button>
          </div>
        </div>

        {/* Tableau principal */}
        <Card className="shadow-sm overflow-hidden">
          <CardContent className="p-0">
            <div className="px-6 py-4 border-b">
              <h2 className="font-semibold text-gray-900">Liste des {filteredPanneaux.length} panneaux</h2>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">N°</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nom du panneau</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type de panneau</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date d'achat</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Coût d'achat (FCFA)</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-l" colSpan={2}>
                      Frais installation (FCFA)
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-l" colSpan={2}>
                      Frais fonctionnement (FCFA)
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider border-l">Total (FCFA)</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                  <tr className="bg-gray-50 border-b text-xs">
                    <th colSpan={5}></th>
                    <th className="px-4 py-2 text-right font-medium text-gray-500 border-l">Montant</th>
                    <th className="px-4 py-2 text-left font-medium text-gray-500">Date</th>
                    <th className="px-4 py-2 text-right font-medium text-gray-500 border-l">Montant</th>
                    <th className="px-4 py-2 text-left font-medium text-gray-500">Période</th>
                    <th colSpan={2}></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredPanneaux.map((panneau) => (
                    <tr key={panneau.id} className="hover:bg-gray-50 transition-colors" data-testid={`panneau-row-${panneau.numero}`}>
                      <td className="px-4 py-3 text-gray-700 font-medium">{panneau.numero}</td>
                      <td className="px-4 py-3">
                        <div className="font-semibold text-gray-900">{panneau.nom}</div>
                        <div className="text-xs text-gray-500">({panneau.quartier})</div>
                      </td>
                      <td className="px-4 py-3">
                        <Badge className={`${TYPE_BADGE_COLORS[panneau.type_panneau] || 'bg-gray-100 text-gray-700'} border font-medium px-3`}>
                          {panneau.type_panneau}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-gray-700">{panneau.date_achat}</td>
                      <td className="px-4 py-3 text-right text-gray-900 font-medium">{formatFCFA(panneau.cout_achat)}</td>
                      <td className="px-4 py-3 text-right text-gray-700 border-l">{formatFCFA(panneau.frais_installation_montant)}</td>
                      <td className="px-4 py-3 text-gray-700">{panneau.frais_installation_date}</td>
                      <td className="px-4 py-3 text-right text-gray-700 border-l">{formatFCFA(panneau.frais_fonctionnement_montant)}</td>
                      <td className="px-4 py-3 text-gray-700">{panneau.frais_fonctionnement_periode}</td>
                      <td className="px-4 py-3 text-right text-gray-900 font-bold border-l">{formatFCFA(panneau.total)}</td>
                      <td className="px-4 py-3 text-center">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0" data-testid={`actions-${panneau.numero}`}>
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              onClick={() => {
                                setEditingPanneau({ ...panneau });
                                setShowEditModal(true);
                              }}
                              data-testid={`edit-panneau-${panneau.numero}`}
                            >
                              <Edit2 className="h-4 w-4 mr-2" />
                              Modifier les tarifs
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))}
                </tbody>
                {/* Total row */}
                <tfoot className="bg-gray-50 border-t-2 border-gray-200">
                  <tr className="font-bold text-gray-900">
                    <td colSpan={4} className="px-4 py-4 text-left">Totaux</td>
                    <td className="px-4 py-4 text-right">{formatFCFA(totals.cout_achat)}</td>
                    <td className="px-4 py-4 text-right border-l">{formatFCFA(totals.frais_installation)}</td>
                    <td></td>
                    <td className="px-4 py-4 text-right border-l">{formatFCFA(totals.frais_fonctionnement)}</td>
                    <td></td>
                    <td className="px-4 py-4 text-right border-l">{formatFCFA(totals.total)}</td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            </div>

            {/* Footer */}
            <div className="px-6 py-3 border-t flex items-center justify-between text-sm text-gray-600">
              <div>Affichage de 1 à {filteredPanneaux.length} sur {filteredPanneaux.length} panneaux</div>
              <div className="flex items-center gap-1">
                <Button variant="outline" size="sm" disabled className="h-8 w-8 p-0">‹</Button>
                <Button size="sm" className="h-8 w-8 p-0 bg-blue-600 hover:bg-blue-700">1</Button>
                <Button variant="outline" size="sm" disabled className="h-8 w-8 p-0">›</Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Modal Modifier les tarifs */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit2 className="h-5 w-5 text-blue-600" />
              Modifier les tarifs - {editingPanneau?.nom}
            </DialogTitle>
            <DialogDescription>Modifiez les coûts et frais associés à ce panneau</DialogDescription>
          </DialogHeader>

          {editingPanneau && (
            <div className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Nom du panneau</Label>
                  <Input
                    value={editingPanneau.nom}
                    onChange={(e) => setEditingPanneau({ ...editingPanneau, nom: e.target.value })}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Quartier</Label>
                  <Input
                    value={editingPanneau.quartier}
                    onChange={(e) => setEditingPanneau({ ...editingPanneau, quartier: e.target.value })}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Type de panneau</Label>
                  <Select
                    value={editingPanneau.type_panneau}
                    onValueChange={(val) => setEditingPanneau({ ...editingPanneau, type_panneau: val })}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="4x3">4x3</SelectItem>
                      <SelectItem value="6x4">6x4</SelectItem>
                      <SelectItem value="8x3">8x3</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Date d'achat</Label>
                  <Input
                    placeholder="JJ/MM/AAAA"
                    value={editingPanneau.date_achat}
                    onChange={(e) => setEditingPanneau({ ...editingPanneau, date_achat: e.target.value })}
                    className="mt-1"
                  />
                </div>
              </div>

              <div className="border-t pt-4">
                <h3 className="font-semibold text-gray-700 mb-3">Coût d'achat</h3>
                <div>
                  <Label>Coût d'achat (FCFA)</Label>
                  <Input
                    type="number"
                    value={editingPanneau.cout_achat}
                    onChange={(e) => setEditingPanneau({ ...editingPanneau, cout_achat: parseFloat(e.target.value) || 0 })}
                    className="mt-1"
                    data-testid="edit-cout-achat"
                  />
                </div>
              </div>

              <div className="border-t pt-4">
                <h3 className="font-semibold text-gray-700 mb-3">Frais d'installation</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Montant (FCFA)</Label>
                    <Input
                      type="number"
                      value={editingPanneau.frais_installation_montant}
                      onChange={(e) => setEditingPanneau({ ...editingPanneau, frais_installation_montant: parseFloat(e.target.value) || 0 })}
                      className="mt-1"
                      data-testid="edit-frais-install"
                    />
                  </div>
                  <div>
                    <Label>Date d'installation</Label>
                    <Input
                      placeholder="JJ/MM/AAAA"
                      value={editingPanneau.frais_installation_date}
                      onChange={(e) => setEditingPanneau({ ...editingPanneau, frais_installation_date: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                </div>
              </div>

              <div className="border-t pt-4">
                <h3 className="font-semibold text-gray-700 mb-3">Frais de fonctionnement</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Montant (FCFA)</Label>
                    <Input
                      type="number"
                      value={editingPanneau.frais_fonctionnement_montant}
                      onChange={(e) => setEditingPanneau({ ...editingPanneau, frais_fonctionnement_montant: parseFloat(e.target.value) || 0 })}
                      className="mt-1"
                      data-testid="edit-frais-fonct"
                    />
                  </div>
                  <div>
                    <Label>Période</Label>
                    <Input
                      placeholder="Ex: Janv. - Avr. 2024"
                      value={editingPanneau.frais_fonctionnement_periode}
                      onChange={(e) => setEditingPanneau({ ...editingPanneau, frais_fonctionnement_periode: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                </div>
              </div>

              <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-blue-900">Total recalculé</span>
                  <span className="text-lg font-bold text-blue-900">
                    {formatFCFA(
                      (parseFloat(editingPanneau.cout_achat) || 0)
                      + (parseFloat(editingPanneau.frais_installation_montant) || 0)
                      + (parseFloat(editingPanneau.frais_fonctionnement_montant) || 0)
                    )} FCFA
                  </span>
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-4 border-t">
                <Button variant="outline" onClick={() => setShowEditModal(false)}>
                  <X className="h-4 w-4 mr-2" />
                  Annuler
                </Button>
                <Button
                  onClick={() => handleSave(editingPanneau)}
                  className="bg-blue-600 hover:bg-blue-700 gap-2"
                  data-testid="save-tarifs-btn"
                >
                  <Save className="h-4 w-4" />
                  Enregistrer
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal Ajouter une dépense */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="h-5 w-5 text-blue-600" />
              Ajouter une dépense
            </DialogTitle>
            <DialogDescription>Enregistrez une nouvelle dépense pour un panneau</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div>
              <Label>Panneau *</Label>
              <Select
                value={newDepense.panneau_id}
                onValueChange={(val) => setNewDepense({ ...newDepense, panneau_id: val })}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Sélectionner un panneau" />
                </SelectTrigger>
                <SelectContent>
                  {panneaux.map(p => (
                    <SelectItem key={p.id} value={p.id}>{p.nom} ({p.quartier})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Type de dépense *</Label>
              <Select
                value={newDepense.type_depense}
                onValueChange={(val) => setNewDepense({ ...newDepense, type_depense: val })}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="achat">Coût d'achat</SelectItem>
                  <SelectItem value="installation">Frais d'installation</SelectItem>
                  <SelectItem value="fonctionnement">Frais de fonctionnement</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Montant (FCFA) *</Label>
              <Input
                type="number"
                placeholder="Ex: 500000"
                value={newDepense.montant}
                onChange={(e) => setNewDepense({ ...newDepense, montant: e.target.value })}
                className="mt-1"
              />
            </div>

            <div>
              <Label>Date / Période</Label>
              <Input
                placeholder="JJ/MM/AAAA ou Janv. - Avr. 2024"
                value={newDepense.date}
                onChange={(e) => setNewDepense({ ...newDepense, date: e.target.value })}
                className="mt-1"
              />
            </div>

            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button variant="outline" onClick={() => setShowAddModal(false)}>Annuler</Button>
              <Button onClick={handleAddDepense} className="bg-blue-600 hover:bg-blue-700">
                Ajouter
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
